package one.devsbox.www.ui.apps.wheel.detail

import androidx.compose.animation.Crossfade
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.drawscope.Fill
import androidx.compose.ui.graphics.drawscope.rotate
import androidx.compose.ui.graphics.drawscope.translate
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.drawText
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import one.devsbox.shared.a.App
import one.devsbox.www.ui.widgets.*
import one.devsbox.www.ui.widgets.LazyImage
import one.devsbox.www.www_portal.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.utils.DataState
import one.devsbox.www.tools.ArgsId
import kotlin.math.cos
import kotlin.math.sin
import androidx.compose.runtime.remember
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntSize
import one.devsbox.www.io.Repository

@Composable
@ComposableNavNode(ArgsId::class)
fun AppWheelDUI() {
    with(AppWheelDCtx.get()) {
        ContentAppWheelDUI()
    }
}

@Composable
context(ctx: AppWheelDCtx)
private fun ContentAppWheelDUI() {
    Box(modifier = Modifier.fillMaxSize().background(MaterialTheme.colorScheme.background)) {
        Crossfade(targetState = ctx.vm.state) { state ->
            when (state) {
                is DataState.Idle -> Unit
                is DataState.Loading -> FullscreenLoadingUI()
                is DataState.Error -> {
                    FullscreenErrorUI(
                        message = state.message,
                        onBack = { ctx.action.back() },
                        onReload = { ctx.action.reload() }
                    )
                }

                is DataState.Result -> Box(modifier = Modifier.fillMaxSize()) {
                    SuccessUI(state.nnvl)
                    ContentActions(
                        contentActions = ctx.vm.contentActions,
                        colorInactive = ctx.colorSecondaryContainer,
                        colorActive = ctx.colorPrimaryContainer
                    )
                }
            }
        }
    }

    if (ctx.vm.showDeleteConfirmation) {
        AlertDialog(
            onDismissRequest = { ctx.action.dismissDeleteConfirmation() },
            title = { Text(stringResource(Res.string.ui_app_instance_detail_delete_confirm_title)) },
            text = { Text(stringResource(Res.string.ui_app_instance_detail_delete_confirm_message)) },
            confirmButton = {
                TextButton(
                    onClick = {
                        ctx.action.confirmDelete()
                    }
                ) {
                    Text(
                        text = stringResource(Res.string.ui_locations_detail_btn_delete),
                        color = MaterialTheme.colorScheme.error
                    )
                }
            },
            dismissButton = {
                TextButton(onClick = { ctx.action.dismissDeleteConfirmation() }) {
                    Text(stringResource(Res.string.ui_devices_edit_deviceeui_cancel))
                }
            }
        )
    }

    if (ctx.vm.showDeleteError) {
        AlertDialog(
            onDismissRequest = { ctx.action.dismissDeleteError() },
            title = { Text(stringResource(Res.string.ui_devices_edit_deviceeui_error)) },
            text = { Text(stringResource(Res.string.ui_app_instance_detail_delete_failed)) },
            confirmButton = {
                TextButton(onClick = { ctx.action.dismissDeleteError() }) {
                    Text(stringResource(Res.string.ui_devices_edit_deviceeui_ok))
                }
            }
        )
    }
}

@Composable
context(ctx: AppWheelDCtx)
private fun SuccessUI(data: AppWheelDVM.Data) {
    val instance = data.appInstance
    val wheel = data.wheelApp

    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(16.dp)
            .verticalScroll(rememberScrollState())
    ) {
        Text(
            text = instance.name.uppercase(),
            style = MaterialTheme.typography.headlineMedium,
            fontWeight = FontWeight.Bold,
            color = MaterialTheme.colorScheme.onSurface
        )

        Spacer(modifier = Modifier.height(16.dp))

        BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
            val isWide = maxWidth >= 1000.dp
            if (isWide) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    Column(
                        modifier = Modifier.weight(1f),
                        verticalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        PreviewGameSection(wheel, modifier = Modifier.fillMaxWidth())
                        PreviewSplashSection(wheel)
                    }
                    Column(
                        modifier = Modifier.weight(1.2f),
                        verticalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        PreviewRewardsSection(wheel)
                    }
                }
            } else {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    PreviewRewardsSection(wheel)
                    PreviewGameSection(wheel, modifier = Modifier.fillMaxWidth())
                    PreviewSplashSection(wheel)
                }
            }
        }

        Spacer(modifier = Modifier.height(250.dp))
    }
}

@Composable
private fun PreviewSplashSection(wheel: App.Wheel) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = Color.Transparent),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            LabelSection(text = stringResource(Res.string.ui_app_instance_detail_splash_screen))
            Spacer(modifier = Modifier.height(16.dp))

            val isLand = wheel.content.theme.isLand
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .aspectRatio(if (isLand) 16f / 9f else 9f / 16f)
                    .clip(RoundedCornerShape(8.dp))
                    .background(Color(wheel.content.theme.backgroundColor)),
                contentAlignment = Alignment.Center
            ) {
                if (wheel.content.theme.backgroundStartup != App.NO_IMAGE) {
                    LazyImage(
                        id = wheel.content.theme.backgroundStartup,
                        modifier = Modifier.fillMaxSize(),
                        contentScale = ContentScale.Crop,
                        alphaBackground = false
                    )
                } else {
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.Center,
                        modifier = Modifier.fillMaxSize().padding(16.dp)
                    ) {
                        Text(
                            text = stringResource(Res.string.ui_app_instance_detail_touch_start),
                            fontSize = 18.sp,
                            fontWeight = FontWeight.Bold,
                            color = Color.White,
                            textAlign = TextAlign.Center,
                            modifier = Modifier
                        )
                    }
                }

            }
        }
    }
}

@Composable
private fun PreviewGameSection(wheel: App.Wheel, modifier: Modifier = Modifier) {
    val items = wheel.content.items.sortedBy { it.position }
    val textMeasurer = rememberTextMeasurer()

    val loadedIcons = items.associate { item ->
        item.idGroup to rememberSectorIcon(item.icon.webp)
    }

    Card(
        modifier = modifier,
        colors = CardDefaults.cardColors(containerColor = Color.Transparent),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            LabelSection(text = stringResource(Res.string.ui_app_instance_detail_game_preview))
            Spacer(modifier = Modifier.height(16.dp))

            val isLand = wheel.content.theme.isLand
            val themeBgColor = Color(wheel.content.theme.backgroundColor)
            val backgroundApp = wheel.content.theme.backgroundApp
            val wheelScaleMultiplier = wheel.content.wheel.scale.toFloat() / 100f
            val wheelPosX = wheel.content.wheel.x.toFloat() / 100f
            val wheelPosY = wheel.content.wheel.y.toFloat() / 100f
            val itemFontSize = wheel.content.theme.itemsFontSize.toFloat() / 100f
            val itemNameY = wheel.content.theme.itemNameY.toFloat() / 100f
            val themeItemIconsScale = wheel.content.theme.iconsScale.toFloat() / 100f
            val themeItemIconsY = wheel.content.theme.iconsY.toFloat() / 100f
            val wheelColor = Color(wheel.content.wheel.color.surface)
            val wheelCenterColor = Color(wheel.content.wheel.color.onSurface)
            val themeTriangleColor = Color(wheel.content.theme.triangleColor)
            val wheelScaleCenter = wheel.content.wheel.scaleCenter.toFloat() / 100f

            BoxWithConstraints(
                modifier = Modifier.fillMaxWidth(),
                contentAlignment = Alignment.Center
            ) {
                val aspectRatio = if (isLand) 16f / 9f else 9f / 16f
                val widthPx = constraints.maxWidth
                val heightPx = constraints.maxHeight

                val boxModifier =
                    if (heightPx > 0 && heightPx < 999999 && (widthPx.toFloat() / heightPx.toFloat() > aspectRatio)) {
                        Modifier.fillMaxHeight().aspectRatio(aspectRatio)
                    } else {
                        Modifier.fillMaxWidth().aspectRatio(aspectRatio)
                    }

                Box(
                    modifier = boxModifier
                        .clip(RoundedCornerShape(8.dp))
                        .background(themeBgColor),
                    contentAlignment = Alignment.Center
                ) {
                    if (backgroundApp > 0 && backgroundApp != App.NO_IMAGE) {
                        LazyImage(
                            id = backgroundApp,
                            modifier = Modifier.fillMaxSize(),
                            contentScale = ContentScale.Crop,
                            alphaBackground = false
                        )
                    }

                    Canvas(
                        modifier = Modifier.fillMaxSize()
                    ) {
                        val wSize = size.minDimension * wheelScaleMultiplier
                        val wCenter = wSize / 2f
                        val xOffset = size.width * wheelPosX - wCenter
                        val yOffset = size.height * wheelPosY - wCenter
                        val itemRot = 360f / items.size.coerceAtLeast(1).toFloat()
                        val itemRotHalf = itemRot / 2f

                        translate(left = xOffset, top = yOffset) {
                            drawCircle(
                                color = wheelColor,
                                center = Offset(wCenter, wCenter),
                                radius = wCenter
                            )

                            items.forEachIndexed { index, item ->
                                rotate(itemRot * index.toFloat(), Offset(wCenter, wCenter)) {
                                    drawArc(
                                        color = Color(item.color.surface),
                                        startAngle = -90f - itemRotHalf,
                                        sweepAngle = itemRot,
                                        useCenter = true,
                                        size = Size(wSize - 4.dp.toPx(), wSize - 4.dp.toPx()),
                                        topLeft = Offset(2.dp.toPx(), 2.dp.toPx())
                                    )
                                }
                            }

                            items.forEachIndexed { index, item ->
                                rotate(itemRot * index.toFloat(), Offset(wCenter, wCenter)) {
                                    if (item.name.trim().isNotEmpty()) {
                                        val textStyle = TextStyle(
                                            fontSize = (wSize * 0.045f * itemFontSize).sp,
                                            color = Color(item.color.onSurface),
                                            fontWeight = FontWeight.Bold,
                                            textAlign = TextAlign.Center
                                        )
                                        val measured = textMeasurer.measure(
                                            text = item.name,
                                            style = textStyle
                                        )
                                        drawText(
                                            textLayoutResult = measured,
                                            topLeft = Offset(
                                                x = wCenter - (measured.size.width / 2f),
                                                y = wCenter * itemNameY
                                            )
                                        )
                                    }
                                }
                            }

                            items.forEachIndexed { index, item ->
                                rotate(itemRot * index.toFloat(), Offset(wCenter, wCenter)) {
                                    val iconBitmap = loadedIcons[item.idGroup]
                                    if (iconBitmap != null) {
                                        val iconSize = wSize * 0.12f * themeItemIconsScale
                                        val iconLeft = wCenter - (iconSize / 2f)
                                        val iconTop = wCenter * themeItemIconsY - (iconSize / 2f)
                                        drawImage(
                                            image = iconBitmap,
                                            dstOffset = IntOffset(
                                                iconLeft.toInt(),
                                                iconTop.toInt()
                                            ),
                                            dstSize = IntSize(iconSize.toInt(), iconSize.toInt())
                                        )
                                    }
                                }
                            }

                            repeat(items.size) { index ->
                                rotate(
                                    itemRot * index.toFloat() - itemRotHalf,
                                    Offset(wCenter, wCenter)
                                ) {
                                    drawLine(
                                        color = wheelColor,
                                        start = Offset(wCenter, wCenter),
                                        end = Offset(wCenter, 0f),
                                        strokeWidth = 2.dp.toPx()
                                    )
                                }
                            }

                            val brandingRadius = (wSize * wheelScaleCenter) / 2f
                            drawCircle(
                                color = wheelCenterColor,
                                center = Offset(wCenter, wCenter),
                                radius = brandingRadius
                            )

                            val pinPath = Path().apply {
                                moveTo(wCenter, 0f)
                                lineTo(wCenter - 10.dp.toPx(), -15.dp.toPx())
                                lineTo(wCenter + 10.dp.toPx(), -15.dp.toPx())
                                close()
                            }
                            drawPath(
                                path = pinPath,
                                color = themeTriangleColor,
                                style = Fill
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun PreviewRewardsSection(wheel: App.Wheel) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = Color.Transparent),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            LabelSection(text = stringResource(Res.string.ui_app_instance_detail_rewards))
            Spacer(modifier = Modifier.height(16.dp))

            Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                wheel.content.items.forEach { item ->
                    Card(
                        modifier = Modifier.fillMaxWidth(),
                        colors = CardDefaults.cardColors(containerColor = Color(item.color.surface)),
                        border = BorderStroke(1.dp, Color(item.color.onSurface).copy(alpha = 0.2f))
                    ) {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(start = 16.dp),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                                Text(
                                    text = item.name.uppercase(),
                                    style = MaterialTheme.typography.titleMedium,
                                    fontWeight = FontWeight.Bold,
                                    color = Color(item.color.onSurface)
                                )
                                Text(
                                    text = stringResource(
                                        Res.string.ui_app_instance_detail_rewards_count,
                                        item.count
                                    ),
                                    style = MaterialTheme.typography.labelMedium,
                                    color = Color(item.color.onSurface).copy(alpha = 0.8f)
                                )
                            }

                            Box(
                                modifier = Modifier
                                    .size(128.dp)
                                    .clip(RoundedCornerShape(4.dp))
                                    .background(Color(item.color.onSurface).copy(alpha = 0.15f)),
                                contentAlignment = Alignment.Center
                            ) {
                                if (item.reward.visual != App.NO_IMAGE) {
                                    LazyImage(
                                        id = item.reward.visual,
                                        modifier = Modifier.fillMaxSize(),
                                        contentScale = ContentScale.Fit,
                                        alphaBackground = false
                                    )
                                } else {
                                    Text(
                                        text = stringResource(Res.string.ui_app_instance_detail_gift),
                                        fontSize = 10.sp,
                                        fontWeight = FontWeight.Bold,
                                        color = Color(item.color.onSurface)
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun rememberSectorIcon(id: Long): ImageBitmap? {
    if (id <= 0 || id == App.NO_IMAGE) return null
    var bitmap by remember(id) { mutableStateOf(Repository.getCached(id)) }
    if (bitmap == null) {
        LaunchedEffect(id) {
            runCatching {
                val fetched = Repository.fetchThumb(id)
                bitmap = fetched
            }
        }
    }
    return bitmap
}
